import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import { IFeaturedArticleCard } from '@models/IFeaturedArticleCard';
import { IMeta } from '@models/IMeta';
import { IProductCard } from '@models/IProductCard';
import { IPromoCard } from '@models/IPromoCard';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import classNames from 'classnames';
import { useInView } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import FeaturedProductsGridTab from './FeaturedProductsGridTab';

interface Props {
  readonly title: string;
  readonly tabs: {
    readonly title: string;
    readonly featureItem:
      | IPromoCard
      | IProductCard
      | IFeaturedArticleCard
      | null;
    readonly otherItems: (IProductCard | null)[];
  }[];
  readonly anchorId?: string;
  readonly meta?: IMeta;
}

const FeaturedProductsGrid = ({ title, tabs, anchorId, meta }: Props) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const isComponentInView = useInView(sectionRef);
  const tiledCards = useMemo(
    () => tabs[activeTabIndex]?.otherItems || [],
    [tabs, activeTabIndex]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Additional Dependencies
  useEffect(() => {
    if (isComponentInView) {
      updateDataLayer({
        event: 'viewItemList',
        ecommerce: {
          item_list_name: title,
          items: tiledCards
            ?.filter((product) => product?.trackingData)
            .map((product) =>
              mapToNewTrackingData(product?.trackingData!, {
                listingName: title,
              })
            ),
        },
      });
    }
  }, [isComponentInView, tabs, tiledCards, title]);

  return (
    <section
      ref={sectionRef}
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-164-Featured-Product-Grid"
      id={`a-${anchorId ?? 'featured-product-grid'}`}
    >
      <SoftLoadWrapper>
        <div className="container">
          <div className="mb-7.5 flex justify-between gap-5 max-xl:flex-col xl:mb-10 xl:items-center">
            <p
              className="h2"
              data-testid="CT-164-Featured-Product-Grid-Title"
              data-epi-property-name={meta?.editHints?.title}
            >
              {title}
            </p>

            <div
              className="flex overflow-hidden"
              data-epi-property-name={meta?.editHints?.tabs}
            >
              <ShadowContainerHorizontal
                className="flex w-full"
                shadowWidth="w-24"
                hasTabbableNestedItems
              >
                <div className="flex w-full gap-5">
                  {tabs.length > 1 &&
                    tabs.map(({ title: tabTitle }, index) => (
                      <button
                        key={tabTitle}
                        className={classNames(
                          'body-two flex items-center justify-center gap-2.5 rounded-[100px] px-4 py-1',
                          activeTabIndex === index
                            ? 'bg-black text-white dark:bg-white dark:text-black'
                            : 'bg-grey-200 hover:bg-grey dark:bg-grey-600 hover:dark:bg-grey-500'
                        )}
                        disabled={activeTabIndex === index}
                        onClick={() => {
                          setActiveTabIndex(index);
                        }}
                        data-testid={`CT-164-Featured-Product-Grid-Tab-${index}`}
                      >
                        {tabTitle}
                      </button>
                    ))}
                </div>
              </ShadowContainerHorizontal>
            </div>
          </div>

          {tabs[activeTabIndex] && (
            <FeaturedProductsGridTab {...tabs[activeTabIndex]} />
          )}
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default FeaturedProductsGrid;
